import { action, computed, makeObservable, observable } from "mobx"

import { OrchestratedQuery } from "./orchestrated-query"

/**
 * Store for monitoring and managing all OrchestratedQuery instances.
 * Provides diagnostics and bulk operations for background queries.
 */
export class OrchestratedQueryStore {
  queries: OrchestratedQuery<any>[] = []
  rpcMetricsEnabled = true // Session-only, NOT persisted

  constructor() {
    makeObservable(this, {
      queries: observable,
      rpcMetricsEnabled: observable,
      registerQuery: action,
      unregisterQuery: action,
      forceExecuteAll: action,
      startAll: action,
      stopAll: action,
      dispose: action,
      enableRpcMetrics: action,
      disableRpcMetrics: action,
      activeQueries: computed,
      runningQueries: computed,
      errorQueries: computed,
      totalQueries: computed,
      totalActiveQueries: computed,
      totalRunningQueries: computed,
      totalErrorQueries: computed,
      performanceStats: computed,
    })
  }

  /**
   * Register a new query for monitoring
   */
  registerQuery(query: OrchestratedQuery<any>): void {
    this.queries.push(query)
    // console.log(`🔍 OrchestratedQueryStore: Registered query "${query.getName}"`)
  }

  /**
   * Unregister a query from monitoring
   */
  unregisterQuery(query: OrchestratedQuery<any>): void {
    const index = this.queries.indexOf(query)
    if (index > -1) {
      this.queries.splice(index, 1)
      // console.log(`🔍 OrchestratedQueryStore: Unregistered query "${query.getName}"`)
    }
  }

  /**
   * Force execute all registered queries
   */
  forceExecuteAll(): void {
    this.queries.forEach((query) => {
      try {
        query.forceExecute()
      } catch (error) {
        console.error(`Failed to force execute query "${query.getName}":`, error)
      }
    })
  }

  /**
   * Start all registered queries
   */
  startAll(): void {
    this.queries.forEach((query) => {
      try {
        query.start()
      } catch (error) {
        console.error(`Failed to start query "${query.getName}":`, error)
      }
    })
  }

  /**
   * Stop all registered queries
   */
  stopAll(): void {
    this.queries.forEach((query) => {
      try {
        query.stop()
      } catch (error) {
        console.error(`Failed to stop query "${query.getName}":`, error)
      }
    })
  }

  /**
   * Dispose all queries and clear the store
   */
  dispose(): void {
    this.queries.forEach((query) => {
      try {
        query.dispose()
      } catch (error) {
        console.error(`Failed to dispose query "${query.getName}":`, error)
      }
    })
    this.queries = []
  }

  /**
   * Get a query by name
   */
  getQueryByName(name: string): OrchestratedQuery<any> | undefined {
    return this.queries.find((query) => query.getName === name)
  }

  /**
   * Get queries by category (can be extended for categorization)
   */
  getQueriesByCategory(category: string): OrchestratedQuery<any>[] {
    // For now, return all queries. Can be extended with category logic
    return this.queries.filter((query) => query.getName.includes(category))
  }

  /**
   * Enable RPC metrics collection for all queries.
   * Session-only - not persisted to storage.
   */
  enableRpcMetrics(): void {
    this.rpcMetricsEnabled = true
  }

  /**
   * Disable RPC metrics collection for all queries.
   * Session-only - not persisted to storage.
   */
  disableRpcMetrics(): void {
    this.rpcMetricsEnabled = false
  }

  // Computed properties for monitoring
  get activeQueries(): OrchestratedQuery<any>[] {
    return this.queries.filter((query) => query.getIsActive)
  }

  get runningQueries(): OrchestratedQuery<any>[] {
    return this.queries.filter((query) => query.getIsRunning)
  }

  get errorQueries(): OrchestratedQuery<any>[] {
    return this.queries.filter((query) => query.getLastError)
  }

  get totalQueries(): number {
    return this.queries.length
  }

  get totalActiveQueries(): number {
    return this.activeQueries.length
  }

  get totalRunningQueries(): number {
    return this.runningQueries.length
  }

  get totalErrorQueries(): number {
    return this.errorQueries.length
  }

  get performanceStats(): PerformanceStats {
    const totalExecutions = this.queries.reduce((sum, query) => sum + query.getExecutionCount, 0)
    const totalExecutionTime = this.queries.reduce((sum, query) => sum + query.getTotalExecutionTime, 0)
    const averageExecutionTime = totalExecutions > 0 ? totalExecutionTime / totalExecutions : 0

    return {
      totalExecutions,
      totalExecutionTime,
      averageExecutionTime,
      totalQueries: this.totalQueries,
      activeQueries: this.totalActiveQueries,
      runningQueries: this.totalRunningQueries,
      errorQueries: this.totalErrorQueries,
    }
  }
}

interface PerformanceStats {
  totalExecutions: number
  totalExecutionTime: number
  averageExecutionTime: number
  totalQueries: number
  activeQueries: number
  runningQueries: number
  errorQueries: number
}
